import { type VariantProps, cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const typographyVariants = cva('', {
  variants: {
    variant: {
      h1: 'text-[32px] text-primary font-serif font-normal tracking-tight leading-tight',
      h2: 'text-3xl text-primary font-serif tracking-tight',
      h3: 'text-2xl text-primary font-serif tracking-normal',
      h4: 'text-xl text-primary font-normal leading-snug',
      h5: 'text-lg text-primary font-medium leading-normal',
      h6: 'text-base text-primary font-normal leading-none',
      'section-title': 'text-base font-medium font-inter leading-5',
      'metric-value':
        'text-[18px] font-semibold font-inter leading-6 tabular-nums',
      p: 'leading-7',
      lead: 'text-xl text-muted-foreground',
      large: 'text-lg font-semibold',
      small: 'text-primary text-sm font-medium leading-none',
      body: 'text-sm leading-normal font-medium',
      'body-sm': 'text-xs leading-normal',
      overline: 'text-[11px] font-medium uppercase tracking-wide leading-4',
      muted: 'text-sm text-muted-foreground',
      code: 'relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold',
    },
  },
  defaultVariants: {
    variant: 'p',
  },
})

type TypographyElement =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'span'
  | 'code'

interface TypographyProps
  extends
    React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof typographyVariants> {
  as?: TypographyElement
}

const variantToElement: Record<
  NonNullable<VariantProps<typeof typographyVariants>['variant']>,
  TypographyElement
> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  'section-title': 'p',
  'metric-value': 'p',
  p: 'p',
  lead: 'p',
  large: 'p',
  small: 'p',
  body: 'p',
  'body-sm': 'p',
  overline: 'span',
  muted: 'p',
  code: 'code',
}

function Typography({
  className,
  variant,
  as,
  children,
  ...props
}: TypographyProps) {
  const Component = as || variantToElement[variant || 'p']

  return (
    <Component
      className={cn(typographyVariants({ variant }), className)}
      {...props}
    >
      {children}
    </Component>
  )
}

export { Typography, typographyVariants }
